// src/components/payroll/PayslipViewer.jsx
//
// Inline payslip list + selectable detail view used by the Payslips
// tab of PayrollMessengerPage. Self-contained — no modals; the detail
// view opens *under* the row that was clicked. Falls back to a skeleton
// while loading and a friendly empty state when there are no payslips.

import React, { useEffect, useState } from "react";
import { Download, Eye, ChevronDown, ChevronUp, FileText } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const authH = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

// ─────────────────────────────────────────────────────
//  Formatting helpers
// ─────────────────────────────────────────────────────
const monthLabel = (raw) => {
  // raw is e.g. "2026-05" or "2026-05-India" or full ISO; render "May 2026"
  if (!raw) return "—";
  const m = String(raw).match(/(\d{4})-(\d{1,2})/);
  if (!m) return raw;
  const [_, y, mo] = m;
  const date = new Date(Number(y), Number(mo) - 1, 1);
  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

const periodLabel = (raw) => {
  // "May 2026 · 1–31"
  if (!raw) return "—";
  const m = String(raw).match(/(\d{4})-(\d{1,2})/);
  if (!m) return raw;
  const [_, y, mo] = m;
  const last = new Date(Number(y), Number(mo), 0).getDate();
  const monthName = new Date(Number(y), Number(mo) - 1, 1)
    .toLocaleDateString(undefined, { month: "long" });
  return `${monthName} ${y} · 1–${last}`;
};

const fmtMoney = (n, currency = "GBP") => {
  if (n == null) return "—";
  const sym = currency === "GBP" ? "£" : currency === "INR" ? "₹" : currency === "USD" ? "$" : "";
  try {
    return `${sym}${Number(n).toLocaleString(undefined, {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
  } catch { return `${sym}${n}`; }
};

// ─────────────────────────────────────────────────────
//  Skeleton
// ─────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <div style={{
      background: "#0d0d0d", border: "0.5px solid #1a1a1a",
      borderRadius: 12, padding: "16px 20px",
      display: "flex", alignItems: "center", gap: 16,
      animation: "kwery-pulse-bg 1.4s ease-in-out infinite",
    }}>
      <div style={{ width: 120, height: 14, borderRadius: 4, background: "#1a1a1a" }} />
      <div style={{ flex: 1 }} />
      <div style={{ width: 90, height: 18, borderRadius: 4, background: "#1a1a1a" }} />
      <div style={{ width: 80, height: 32, borderRadius: 8, background: "#1a1a1a" }} />
      <div style={{ width: 110, height: 32, borderRadius: 8, background: "#1a1a1a" }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────
//  PDF download helper — authenticated fetch then click
// ─────────────────────────────────────────────────────
async function downloadPayslipPdf(payslipId, monthRaw) {
  try {
    const res = await fetch(`${API}/api/payroll/payslips/${payslipId}/pdf`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (!res.ok) throw new Error(`PDF unavailable (HTTP ${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payslip-${monthRaw || "month"}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

// ─────────────────────────────────────────────────────
//  Detail card (inline expand)
// ─────────────────────────────────────────────────────
function PayslipDetail({ payslip, employeeName, onClose }) {
  const [dlState, setDlState] = useState({ busy: false, error: null });
  const p = payslip || {};
  const earnings   = p.earnings   || {};
  const deductions = p.deductions || {};
  const currency   = p.currency   || "GBP";

  // Normalise deduction keys so we can render specific lines
  const dPaye    = deductions.paye    ?? deductions.tax ?? deductions.PAYE ?? null;
  const dNi      = deductions.ni      ?? deductions.national_insurance ?? deductions.NI ?? null;
  const dPension = deductions.pension ?? deductions.pension_ee ?? deductions.ee_pension ?? null;
  const dSL      = deductions.student_loan ?? deductions.studentLoan ?? null;
  const dOther   = Object.entries(deductions).filter(([k]) =>
    ![
      "paye", "tax", "PAYE",
      "ni", "national_insurance", "NI",
      "pension", "pension_ee", "ee_pension",
      "student_loan", "studentLoan",
    ].includes(k));

  const onDownload = async () => {
    setDlState({ busy: true, error: null });
    const r = await downloadPayslipPdf(p.id, p.month);
    setDlState({ busy: false, error: r.ok ? null : r.error });
  };

  return (
    <div style={{
      background: "#0a0a0a", border: "0.5px solid #1e1e1e",
      borderTop: "none", borderRadius: "0 0 12px 12px",
      padding: 24, marginTop: -8, marginBottom: 12,
      animation: "kwery-slide-down 0.25s ease-out",
    }}>
      <style>{`
        @keyframes kwery-slide-down { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes kwery-pulse-bg { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, color: "#fff", fontWeight: 500 }}>{employeeName || "Employee"}</div>
          <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{periodLabel(p.month)}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "transparent", border: "none", color: "#666",
            cursor: "pointer", fontSize: 12, padding: 4,
          }}
        >Hide</button>
      </div>

      {/* Earnings + Deductions tables */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div>
          <div style={{ fontSize: 10, color: "#444", textTransform: "uppercase",
                         letterSpacing: 2, marginBottom: 8 }}>Earnings</div>
          <table style={{ width: "100%", fontSize: 13 }}>
            <tbody>
              {Object.entries(earnings).map(([k, v]) => (
                <tr key={k}>
                  <td style={{ color: "#999", padding: "5px 0" }}>
                    {k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </td>
                  <td style={{ color: "#fff", textAlign: "right", padding: "5px 0" }}>
                    {fmtMoney(v, currency)}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: "0.5px solid #1e1e1e" }}>
                <td style={{ color: "#888", padding: "8px 0", fontWeight: 500 }}>Gross</td>
                <td style={{ color: "#fff", textAlign: "right", padding: "8px 0", fontWeight: 500 }}>
                  {fmtMoney(p.gross, currency)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div>
          <div style={{ fontSize: 10, color: "#444", textTransform: "uppercase",
                         letterSpacing: 2, marginBottom: 8 }}>Deductions</div>
          <table style={{ width: "100%", fontSize: 13 }}>
            <tbody>
              {dPaye    != null && <DedRow label="PAYE"          value={dPaye}    currency={currency} />}
              {dNi      != null && <DedRow label="National Insurance" value={dNi} currency={currency} />}
              {dPension != null && <DedRow label="Pension"       value={dPension} currency={currency} />}
              {dSL      != null && <DedRow label="Student Loan"  value={dSL}      currency={currency} />}
              {dOther.map(([k, v]) => (
                <DedRow key={k}
                        label={k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                        value={v} currency={currency} />
              ))}
              <tr style={{ borderTop: "0.5px solid #1e1e1e" }}>
                <td style={{ color: "#888", padding: "8px 0", fontWeight: 500 }}>Total deductions</td>
                <td style={{ color: "#fff", textAlign: "right", padding: "8px 0", fontWeight: 500 }}>
                  {fmtMoney(
                    Object.values(deductions).reduce(
                      (s, v) => s + (Number(v) || 0), 0
                    ), currency
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Net pay — large, neon */}
      <div style={{
        marginTop: 20, padding: "20px 24px",
        background: "rgba(0,255,153,0.04)",
        border: "0.5px solid rgba(0,255,153,0.2)",
        borderRadius: 12,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <div style={{ fontSize: 11, color: "#00FF99", letterSpacing: 2,
                         textTransform: "uppercase", fontWeight: 600 }}>Net pay</div>
          <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
            Paid into account ending {p.account_last_4 || "•••• ••••"}
          </div>
        </div>
        <div style={{ fontSize: 28, color: "#00FF99", fontWeight: 500 }}>
          {fmtMoney(p.net, currency)}
        </div>
      </div>

      {/* Download button */}
      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 12, alignItems: "center" }}>
        {dlState.error && (
          <span style={{ fontSize: 12, color: "#E24B4A" }}>
            {dlState.error}
          </span>
        )}
        <button
          type="button"
          onClick={onDownload}
          disabled={dlState.busy}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "10px 18px",
            background: "transparent",
            border: "0.5px solid rgba(0,255,153,0.4)",
            borderRadius: 8, color: "#00FF99",
            fontSize: 13, fontWeight: 500, cursor: dlState.busy ? "default" : "pointer",
            opacity: dlState.busy ? 0.5 : 1,
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { if (!dlState.busy) e.currentTarget.style.background = "rgba(0,255,153,0.08)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          <Download size={14} /> {dlState.busy ? "Preparing…" : "Download PDF"}
        </button>
      </div>
    </div>
  );
}

function DedRow({ label, value, currency }) {
  return (
    <tr>
      <td style={{ color: "#999", padding: "5px 0" }}>{label}</td>
      <td style={{ color: "#fff", textAlign: "right", padding: "5px 0" }}>
        {fmtMoney(value, currency)}
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────
//  Main viewer
// ─────────────────────────────────────────────────────
export default function PayslipViewer() {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [openId, setOpenId]     = useState(null);
  const [employee, setEmployee] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [pRes, profRes] = await Promise.all([
          fetch(`${API}/api/payroll/mine`,  { headers: authH() }).then(r => r.json()).catch(() => []),
          fetch(`${API}/api/profile/me`,    { headers: authH() }).then(r => r.json()).catch(() => null),
        ]);
        if (cancelled) return;
        const list = Array.isArray(pRes) ? pRes : (pRes?.items || pRes?.payslips || pRes?.data || []);
        // Sort by month desc so newest is first
        list.sort((a, b) => String(b.month || "").localeCompare(String(a.month || "")));
        setPayslips(list);
        const data = profRes?.data || profRes || {};
        setEmployee(data?.name || data?.full_name || "");
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load payslips");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Render ──
  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <SkeletonRow /><SkeletonRow /><SkeletonRow />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: "rgba(226,75,74,0.06)",
        border: "0.5px solid rgba(226,75,74,0.3)",
        borderRadius: 12, padding: "16px 20px",
        color: "#E24B4A", fontSize: 13,
      }}>
        Couldn't load payslips: {error}
      </div>
    );
  }

  if (!payslips.length) {
    return (
      <div style={{
        background: "#0a0a0a", border: "0.5px solid #1a1a1a",
        borderRadius: 14, padding: "48px 32px", textAlign: "center",
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          background: "#0d0d0d", border: "0.5px solid #1e1e1e",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: "#444", marginBottom: 16,
        }}>
          <FileText size={28} />
        </div>
        <div style={{ fontSize: 15, color: "#fff", marginBottom: 6 }}>No payslips yet</div>
        <div style={{ fontSize: 13, color: "#666", lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
          Your first payslip will appear here after your first payroll run.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {payslips.map((p) => {
        const isOpen = openId === p.id;
        return (
          <div key={p.id}>
            {/* Row */}
            <div style={{
              background: "#0d0d0d",
              border: "0.5px solid " + (isOpen ? "#1e1e1e" : "#1a1a1a"),
              borderRadius: isOpen ? "12px 12px 0 0" : 12,
              padding: "16px 20px",
              display: "flex", alignItems: "center", gap: 16,
              transition: "border-color 0.15s",
            }}>
              <div style={{ minWidth: 140 }}>
                <div style={{ fontSize: 14, color: "#fff", fontWeight: 500 }}>{monthLabel(p.month)}</div>
                <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{p.currency || "GBP"} · Payroll period</div>
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "#444", textTransform: "uppercase", letterSpacing: 1 }}>Net pay</div>
                <div style={{ fontSize: 18, color: "#00FF99", fontWeight: 500 }}>
                  {fmtMoney(p.net, p.currency)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : p.id)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 14px",
                  background: "transparent",
                  border: "0.5px solid #222",
                  borderRadius: 8, color: "#888",
                  fontSize: 13, cursor: "pointer",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#333"; e.currentTarget.style.color = "#bbb"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#222"; e.currentTarget.style.color = "#888"; }}
              >
                {isOpen ? <ChevronUp size={14} /> : <Eye size={14} />}
                {isOpen ? "Hide" : "View"}
              </button>
              <button
                type="button"
                onClick={() => downloadPayslipPdf(p.id, p.month)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 14px",
                  background: "rgba(0,255,153,0.06)",
                  border: "0.5px solid rgba(0,255,153,0.3)",
                  borderRadius: 8, color: "#00FF99",
                  fontSize: 13, cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,255,153,0.12)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(0,255,153,0.06)"}
              >
                <Download size={14} /> Download PDF
              </button>
            </div>

            {/* Detail */}
            {isOpen && (
              <PayslipDetail
                payslip={p}
                employeeName={employee}
                onClose={() => setOpenId(null)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
